/**
 * ConflictCard — displays a single conflict/duplicate record.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { BuddyGroupConflict, ConflictStatus, ConflictType } from '../types/conflict';

const statusVariant: Record<ConflictStatus, 'default' | 'secondary' | 'outline'> = {
  pending: 'default',
  dismissed: 'outline',
  resolved: 'secondary',
};

const typeLabel: Record<ConflictType, string> = {
  card_number: 'Member Number',
  name_dob: 'Name + DOB',
};

interface ConflictCardProps {
  conflict: BuddyGroupConflict;
  canDismiss: boolean;
  canResolve: boolean;
  onDismiss: (conflict: BuddyGroupConflict) => void;
  onResolve: (conflict: BuddyGroupConflict) => void;
}

export function ConflictCard({ conflict, canDismiss, canResolve, onDismiss, onResolve }: ConflictCardProps) {
  const isResolved = conflict.status !== 'pending';

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${isResolved ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{typeLabel[conflict.conflict_type]}</Badge>
          <Badge variant={statusVariant[conflict.status]}>{conflict.status}</Badge>
        </div>
        {!isResolved && (
          <div className="flex items-center gap-1">
            {canDismiss && (
              <Button variant="ghost" size="sm" onClick={() => onDismiss(conflict)}>
                Dismiss
              </Button>
            )}
            {canResolve && (
              <Button variant="outline" size="sm" onClick={() => onResolve(conflict)}>
                Resolve
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="text-sm">
        <p className="font-medium text-foreground">Duplicate: {conflict.duplicate_value}</p>
        <p className="text-muted-foreground mt-1">
          Members: {conflict.member_names.join(', ')}
        </p>
        <p className="text-muted-foreground text-xs mt-1">
          {conflict.registration_ids.length} registration{conflict.registration_ids.length !== 1 ? 's' : ''} affected
        </p>
      </div>

      {conflict.resolution_note && (
        <p className="text-xs text-muted-foreground border-t pt-2">
          Note: {conflict.resolution_note}
        </p>
      )}
    </div>
  );
}
